<script setup lang="ts">
import {computed, onUnmounted, ref} from "vue";
import {Plus, Search} from "@lucide/vue";
import {drugInteractionApi} from "@/container";
import {Input} from "@/core/components/ui/input";
import {VidalEntry} from "@/medication-checkup/interfaces/patient-profile";

/**
 * A search in the Vidal referential, for one kind of entry: the field and the
 * results under it.
 *
 * The search is the one the patient file already uses: three characters at
 * least, half a second of quiet before asking. Picking a result hands it to
 * the parent and clears the field. Where the parent takes words of its own,
 * the last row adds what was typed, as typed — for an entry Vidal does not
 * name, or names otherwise.
 */

const props = defineProps<{
    kind: 'allergy' | 'pathology' | 'side_effect';
    /**
     * Whether a result is already picked, shown but not offered again.
     */
    picked?: (entry: VidalEntry) => boolean;
    /**
     * Whether what was typed may be added as it is.
     */
    free?: boolean;
}>();

const emit = defineEmits<{
    pick: [entry: VidalEntry];
    add: [typed: string];
    dismiss: [];
}>();

/**
 * The words of each kind: the placeholder of the search and the title of its
 * results.
 */
const WORDS = {
    allergy: {placeholder: 'Rechercher une allergie (référentiel Vidal)…', results: 'Allergies Vidal'},
    pathology: {placeholder: 'Rechercher une pathologie (référentiel Vidal)…', results: 'Pathologies Vidal'},
    side_effect: {placeholder: 'Rechercher un effet indésirable (référentiel Vidal)…', results: 'Effets indésirables Vidal'},
};

const MIN_QUERY_LENGTH = 3;

const DEBOUNCE_MS = 500;

const words = computed(() => WORDS[props.kind]);

const query = ref('');
const loading = ref(false);
const results = ref<VidalEntry[] | null>(null);
const focused = ref(false);

let timer: ReturnType<typeof setTimeout> | null = null;

onUnmounted(() => {
    if (timer) {
        clearTimeout(timer);
    }
});

/**
 * The results panel shows while the search field is focused and the query is
 * long enough to have asked.
 */
const showsResults = computed(() => focused.value && query.value.length >= MIN_QUERY_LENGTH);

/**
 * The entries Vidal finds, whose id the allergies and pathologies carry as a
 * number.
 */
const fetch = (search: string): Promise<Array<{vidal_id: string | number, title: string}>> => {
    const api = drugInteractionApi();

    switch (props.kind) {
        case 'allergy':
            return api.allergies(search);
        case 'pathology':
            return api.pathologies(search);
        case 'side_effect':
            return api.sideEffects(search);
    }
};

const search = (typed: string | number) => {
    query.value = String(typed);

    if (timer) {
        clearTimeout(timer);
    }

    if (query.value.length < MIN_QUERY_LENGTH) {
        results.value = null;

        return;
    }

    timer = setTimeout(async () => {
        loading.value = true;

        try {
            const found = await fetch(query.value);

            results.value = found.map(entry => ({vidal_id: String(entry.vidal_id), title: entry.title}));
        } finally {
            loading.value = false;
        }
    }, DEBOUNCE_MS);
};

const clear = () => {
    query.value = '';
    results.value = null;
};

const pick = (entry: VidalEntry) => {
    emit('pick', entry);
    clear();
};

const add = () => {
    emit('add', query.value.trim());
    clear();
};

/**
 * Leaving the field closes the results after a beat, long enough for a click
 * on one of them to land first.
 */
const blur = () => {
    setTimeout(() => focused.value = false, 150);
};
</script>

<template>
  <div class="relative">
    <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
    <Input
      :model-value="query"
      :placeholder="words.placeholder"
      class="pl-8 bg-white focus-visible:ring-primary"
      autofocus
      @update:model-value="search"
      @focus="focused = true"
      @blur="blur"
      @keydown.escape="emit('dismiss')"
    />

    <div
      v-if="showsResults"
      class="absolute top-full left-0 z-50 mt-1 min-w-[280px] max-w-full flex flex-col gap-0.5 p-1.5 bg-white border border-slate-200 rounded-lg shadow-lg"
    >
      <span class="px-2 pt-1 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {{ words.results }} — « {{ query }} »
      </span>

      <span v-if="loading" class="px-2 py-1.5 text-sm text-muted-foreground">Recherche en cours…</span>

      <template v-else-if="results">
        <!-- Grabbing the scrollbar must not take the focus from the field, which closes the results. -->
        <div
          v-if="results.length > 0"
          class="flex flex-col gap-0.5 max-h-56 overflow-y-auto"
          @mousedown.prevent
        >
          <button
            v-for="result in results"
            :key="result.vidal_id"
            class="shrink-0 px-2 py-1.5 rounded-md text-left text-sm hover:bg-gray-light disabled:opacity-60"
            :disabled="picked?.(result)"
            @mousedown.prevent="pick(result)"
          >
            {{ result.title }}
          </button>
        </div>

        <span v-else class="px-2 py-1.5 text-sm text-muted-foreground">Aucun résultat</span>
      </template>

      <button
        v-if="free && query.trim() !== ''"
        class="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-sm text-primary hover:bg-gray-light"
        :class="{'mt-0.5 pt-2 border-t border-gray-light rounded-t-none': !loading && results !== null}"
        @mousedown.prevent="add"
      >
        <Plus class="w-3.5 h-3.5 shrink-0" />
        Ajouter « {{ query.trim() }} »
      </button>
    </div>
  </div>
</template>
